import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Button } from '@mui/material';
import FullscreenOutlined from '@mui/icons-material/FullscreenOutlined';
import { BrandLogo, theme } from '@zilian/ui';
import { api, ApiError } from '../api';
import { amount } from '../ui';

/**
 * Warehouse picking board for a factory TV, read from a few metres away and never touched.
 *   - 备货队列: every order still to ship, most urgent first. Each order is a card whose
 *     border says how urgent it is (red 逾期, amber 今日), with one row per item: what is left
 *     to pick, how much has shipped, and whether stock covers it.
 *   - 按商品汇总: how much of each item to pick across all orders (batch picking), against
 *     the stock that can be used for it.
 * Stock is handed out by urgency: a line first uses what is reserved (占用) for it, then free
 * stock, so 缺 always lands on the least urgent orders. A newly confirmed order is announced
 * with a chime and a banner for a few seconds. Long columns scroll on their own; data
 * refreshes every 30 s.
 */

interface Line {
  skuId: string;
  name: string;
  spec: string;
  unit: string;
  quantity: string;
  shipped: string;
  remaining: string;
  reserved: string;
}
interface Order {
  id: string;
  number: string;
  source: 'PLATFORM' | 'LOCAL';
  customer: string;
  deliveryDate: string;
  readyAt: string;
  lines: Line[];
}
interface Stock {
  onHand: string;
  reserved: string;
}
type Level = 'late' | 'today' | 'later';
type Due = { label: string; level: Level; rank: number };
/** A line with its stock worked out: what is set aside, what free stock covers, what is short. */
type Planned = Line & { remainingN: number; shippedN: number; ownN: number; shortN: number };
type Ranked = Omit<Order, 'lines'> & { due: Due; lines: Planned[] };

const REFRESH_MS = 30_000;
const CALL_MS = 12_000; // how long a new order holds the banner
const NEW_MS = 30 * 60_000; // an order is tagged 新 for 30 minutes after it became ready

const P = theme.palette;
// The platform palette, with colour kept for urgency and stock.
const C = {
  page: P.background.default,
  panel: P.background.paper,
  line: P.divider,
  head: '#f8fafb',
  text: P.text.primary,
  muted: P.text.secondary,
  brand: P.primary.main,
  brandSoft: P.primary.light,
  late: '#c4372c',
  lateSoft: '#fdeceb',
  today: '#9a5b06',
  todayEdge: '#e3a33b',
  todaySoft: '#fdf4e3',
  ok: '#1d7348',
  okSoft: '#e7f4ec',
  info: '#2c6489',
  infoSoft: '#e6f0f7',
  neutralSoft: '#eef1f3',
  track: '#e9eef0',
  reservedFill: '#9ccbc3',
};
const LEVEL: Record<Level, { fg: string; soft: string; edge: string }> = {
  late: { fg: C.late, soft: C.lateSoft, edge: C.late },
  today: { fg: C.today, soft: C.todaySoft, edge: C.todayEdge },
  later: { fg: C.muted, soft: C.head, edge: C.line },
};
// Type sizes scale with the screen so a 1080p TV and a 4K one read the same from afar.
const F = {
  tiny: 'max(12px, 0.72vw)',
  small: 'max(13px, 0.82vw)',
  body: 'max(15px, 0.98vw)',
  item: 'max(16px, 1.12vw)',
  title: 'max(20px, 1.5vw)',
  qty: 'max(22px, 1.62vw)',
  kpi: 'max(26px, 2.2vw)',
};
// One column grid shared by every order card, so rows line up from card to card.
const COLS = 'minmax(0, 2.2fr) minmax(0, 1.8fr) minmax(0, 1fr) minmax(0, 1.5fr) minmax(0, 0.9fr)';

const day = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const daysBetween = (from: string, to: string) =>
  Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000);
const clock = (d: Date) => d.toLocaleTimeString('zh-CN', { hour12: false });
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const shortNumber = (o: { source: Order['source']; number: string }) =>
  `${o.source === 'LOCAL' ? '登记单 ' : '单号 '}${o.number.slice(-8)}`;

/** Due-date wording and level, from the most to the least urgent. */
function urgency(deliveryDate: string, today: string): Due {
  const diff = daysBetween(today, deliveryDate);
  if (diff < 0) return { label: `逾期 ${-diff} 天`, level: 'late', rank: 0 };
  if (diff === 0) return { label: '今日交货', level: 'today', rank: 1 };
  if (diff === 1) return { label: '明日交货', level: 'later', rank: 2 };
  const [, m, d] = deliveryDate.split('-').map(Number);
  return { label: `${m}月${d}日交货`, level: 'later', rank: 3 };
}

/**
 * Hands stock to lines in board order (most urgent first). Each line takes what is reserved
 * for it, then free stock (on hand less every reservation); whatever is left is short.
 */
function plan(orders: (Order & { due: Due })[], stock: Record<string, Stock>): Ranked[] {
  const free = new Map(
    Object.entries(stock).map(([id, s]) => [id, Number(s.onHand) - Number(s.reserved)]),
  );
  return orders.map((o) => ({
    ...o,
    lines: o.lines.map((l) => {
      const remainingN = Number(l.remaining);
      const ownN = Math.min(Number(l.reserved), remainingN);
      const pool = Math.max(0, free.get(l.skuId) ?? 0);
      const take = Math.min(pool, remainingN - ownN);
      free.set(l.skuId, pool - take);
      return {
        ...l,
        remainingN,
        shippedN: Number(l.shipped),
        ownN,
        shortN: remainingN - ownN - take,
      };
    }),
  }));
}

/** A short two-tone chime for a new order; browsers only allow it after a click. */
function chime(ctx: AudioContext | null) {
  if (!ctx) return;
  [880, 1320].forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = freq;
    const at = ctx.currentTime + i * 0.22;
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(0.25, at + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.5);
    osc.connect(gain).connect(ctx.destination);
    osc.start(at);
    osc.stop(at + 0.55);
  });
}

/**
 * Scrolls its content on its own when it is taller than the box: holds at the top, glides to
 * the end at a reading pace, holds, then returns to the top. Content that fits stays still.
 */
function AutoScroll({ children, watch }: { children: ReactNode; watch: string }) {
  const box = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState(0);
  useLayoutEffect(() => {
    const measure = () =>
      setOverflow(
        Math.max(0, (inner.current?.scrollHeight ?? 0) - (box.current?.clientHeight ?? 0)),
      );
    measure();
    const observer = new ResizeObserver(measure);
    if (box.current) observer.observe(box.current);
    if (inner.current) observer.observe(inner.current);
    return () => observer.disconnect();
  }, [watch]);
  useEffect(() => {
    const el = inner.current;
    if (!el) return;
    el.style.transition = 'none';
    el.style.transform = 'translateY(0)';
    if (!overflow || reducedMotion()) return;
    const HOLD = 5_000;
    const glide = Math.max(4_000, (overflow / 32) * 1000); // about 32 px per second
    let timer = 0;
    const cycle = () => {
      timer = window.setTimeout(() => {
        el.style.transition = `transform ${glide}ms linear`;
        el.style.transform = `translateY(-${overflow}px)`;
        timer = window.setTimeout(() => {
          el.style.transition = 'transform 600ms ease';
          el.style.transform = 'translateY(0)';
          timer = window.setTimeout(cycle, 600);
        }, glide + HOLD);
      }, HOLD);
    };
    cycle();
    return () => clearTimeout(timer);
  }, [overflow]);
  return (
    <Box ref={box} sx={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
      <Box ref={inner} sx={{ willChange: 'transform' }}>
        {children}
      </Box>
      {overflow > 0 && (
        // A soft fade at the bottom edge says "there is more".
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '5vh',
            background: `linear-gradient(to bottom, transparent, ${C.panel})`,
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
}

/** A state label at TV size, styled like the platform's Tag: soft fill and a leading dot. */
function Badge(p: { fg: string; bg: string; children: ReactNode; solid?: boolean; dot?: boolean }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4vw',
        px: '0.6vw',
        py: '0.35vh',
        borderRadius: 1,
        bgcolor: p.solid ? p.fg : p.bg,
        color: p.solid ? '#fff' : p.fg,
        fontSize: F.small,
        fontWeight: 700,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        flex: 'none',
      }}
    >
      {p.dot !== false && (
        <Box
          component="span"
          sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'currentColor', flex: 'none' }}
        />
      )}
      {p.children}
    </Box>
  );
}

function Panel(p: { title: string; count?: ReactNode; extra?: ReactNode; children: ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: C.panel,
        border: `1px solid ${C.line}`,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: '1vw',
          height: '5.6vh',
          minHeight: 44,
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '0.6vw',
          borderBottom: `1px solid ${C.line}`,
        }}
      >
        <Box sx={{ fontSize: F.body, fontWeight: 700 }}>{p.title}</Box>
        {p.count !== undefined && <Box sx={{ fontSize: F.small, color: C.muted }}>{p.count}</Box>}
        {p.extra && <Box sx={{ ml: 'auto', fontSize: F.small, color: C.muted }}>{p.extra}</Box>}
      </Box>
      {p.children}
    </Box>
  );
}

/** Stock state of one line: reserved for it, covered by free stock, or short by how much. */
function StockTag({ line }: { line: Planned }) {
  if (line.shortN > 0)
    return (
      <Badge fg={C.late} bg={C.lateSoft}>
        缺 {amount(line.shortN)}
      </Badge>
    );
  if (line.ownN >= line.remainingN)
    return (
      <Badge fg={C.info} bg={C.infoSoft}>
        已占用
      </Badge>
    );
  return (
    <Badge fg={C.ok} bg={C.okSoft}>
      可备齐
    </Badge>
  );
}

/** Shipped so far (solid) and reserved (light) against the whole line. */
function Progress({ line }: { line: Planned }) {
  const total = line.shippedN + line.remainingN;
  const pct = (n: number) => `${total > 0 ? (n / total) * 100 : 0}%`;
  const caption =
    line.shippedN > 0 || line.ownN > 0
      ? [
          line.shippedN > 0 && `已发 ${amount(line.shippedN)}`,
          line.ownN > 0 && `已占用 ${amount(line.ownN)}`,
        ]
          .filter(Boolean)
          .join(' · ')
      : '未发货';
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '0.5vw',
          fontSize: F.tiny,
          color: C.muted,
          mb: '0.5vh',
          whiteSpace: 'nowrap',
        }}
      >
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {caption}
        </Box>
        <Box component="span">共 {amount(total)}</Box>
      </Box>
      <Box
        sx={{
          height: 8,
          borderRadius: 1,
          bgcolor: C.track,
          overflow: 'hidden',
          display: 'flex',
        }}
      >
        <Box sx={{ width: pct(line.shippedN), bgcolor: C.brand }} />
        <Box sx={{ width: pct(line.ownN), bgcolor: C.reservedFill }} />
      </Box>
    </Box>
  );
}

function OrderCard({ order, now, arrived }: { order: Ranked; now: Date; arrived?: number }) {
  const tone = LEVEL[order.due.level];
  const urgent = order.due.level !== 'later';
  const ready = order.lines.filter((l) => l.shortN === 0).length;
  const fresh = now.getTime() - Date.parse(order.readyAt) < NEW_MS;
  const justArrived = arrived !== undefined && now.getTime() - arrived < 60_000;
  return (
    <Box
      sx={{
        border: `${urgent ? 2 : 1}px solid ${tone.edge}`,
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: C.panel,
        boxShadow: justArrived ? `0 0 0 3px ${C.brandSoft}` : 'none',
        transition: 'box-shadow 1s ease',
      }}
    >
      {/* Who it is for, how urgent, and how much of it stock covers. */}
      <Box
        sx={{
          px: '1vw',
          py: '1.2vh',
          display: 'flex',
          alignItems: 'center',
          gap: '0.7vw',
          bgcolor: tone.soft,
          borderBottom: `1px solid ${urgent ? tone.edge : C.line}`,
        }}
      >
        <Badge fg={tone.fg} bg={C.neutralSoft} solid={urgent}>
          {order.due.label}
        </Badge>
        <Box
          sx={{
            fontSize: F.title,
            fontWeight: 800,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}
        >
          {order.customer}
        </Box>
        {fresh && (
          <Badge fg={C.brand} bg={C.brandSoft} dot={false}>
            新
          </Badge>
        )}
        <Box sx={{ fontSize: F.small, color: C.muted, whiteSpace: 'nowrap', flex: 'none' }}>
          {shortNumber(order)}
        </Box>
        <Box
          sx={{
            ml: 'auto',
            flex: 'none',
            fontSize: F.small,
            fontWeight: 700,
            color: ready === order.lines.length ? C.ok : C.late,
            whiteSpace: 'nowrap',
          }}
        >
          {ready === order.lines.length
            ? `${order.lines.length} 项均可备齐`
            : `可备齐 ${ready}/${order.lines.length} 项`}
        </Box>
      </Box>
      {/* Items: one aligned row each. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: COLS,
          columnGap: '1.2vw',
          px: '1vw',
          py: '0.7vh',
          fontSize: F.tiny,
          color: C.muted,
          borderBottom: `1px solid ${C.line}`,
        }}
      >
        <Box>商品</Box>
        <Box>规格</Box>
        <Box sx={{ textAlign: 'right' }}>待发数量</Box>
        <Box>发货进度</Box>
        <Box sx={{ textAlign: 'right' }}>库存</Box>
      </Box>
      {order.lines.map((l, i) => (
        <Box
          key={i}
          sx={{
            display: 'grid',
            gridTemplateColumns: COLS,
            columnGap: '1.2vw',
            alignItems: 'center',
            px: '1vw',
            py: '1.1vh',
            borderTop: i ? `1px solid ${C.line}` : 'none',
          }}
        >
          <Box
            sx={{
              fontSize: F.item,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {l.name}
          </Box>
          <Box
            sx={{
              fontSize: F.small,
              color: C.muted,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {l.spec || '—'}
          </Box>
          <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
            <Box component="span" sx={{ fontSize: F.qty, fontWeight: 800, lineHeight: 1 }}>
              {amount(l.remainingN)}
            </Box>
            <Box component="span" sx={{ ml: '0.3vw', fontSize: F.small, color: C.muted }}>
              {l.unit}
            </Box>
          </Box>
          <Progress line={l} />
          <Box sx={{ textAlign: 'right' }}>
            <StockTag line={l} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/** Totals to pick per item across all orders, short items first, against usable stock. */
function Totals({ orders, stock }: { orders: Ranked[]; stock: Record<string, Stock> }) {
  const map = new Map<
    string,
    {
      name: string;
      spec: string;
      unit: string;
      total: number;
      short: number;
      ownOnBoard: number;
      customers: string[];
    }
  >();
  for (const o of orders)
    for (const l of o.lines) {
      const item = map.get(l.skuId) ?? {
        name: l.name,
        spec: l.spec,
        unit: l.unit,
        total: 0,
        short: 0,
        ownOnBoard: 0,
        customers: [],
      };
      item.total += l.remainingN;
      item.short += l.shortN;
      item.ownOnBoard += l.ownN;
      if (!item.customers.includes(o.customer)) item.customers.push(o.customer);
      map.set(l.skuId, item);
    }
  const items = [...map.entries()]
    .map(([skuId, it]) => {
      const s = stock[skuId];
      // Usable for this board: free stock plus what is reserved for the orders on it.
      const usable = s ? Number(s.onHand) - Number(s.reserved) + it.ownOnBoard : 0;
      return { ...it, usable: Math.max(0, usable) };
    })
    .sort((x, y) => Number(y.short > 0) - Number(x.short > 0) || y.total - x.total);
  const shortCount = items.filter((it) => it.short > 0).length;
  return (
    <Panel
      title="按商品汇总"
      count={`${items.length} 种`}
      extra={
        shortCount ? <Box sx={{ color: C.late, fontWeight: 700 }}>{shortCount} 种缺货</Box> : null
      }
    >
      <AutoScroll watch={items.map((it) => `${it.name}:${it.total}:${it.short}`).join('|')}>
        <Box sx={{ p: '0.8vw', display: 'grid', gap: '0.8vw' }}>
          {items.map((it, i) => (
            <Box
              key={i}
              sx={{
                border: `1px solid ${C.line}`,
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: '0.9vw', py: '1.1vh', display: 'flex', gap: '0.8vw' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      fontSize: F.item,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {it.name}
                  </Box>
                  <Box
                    sx={{
                      mt: '0.3vh',
                      fontSize: F.tiny,
                      color: C.muted,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {it.spec || '—'}
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap', flex: 'none' }}>
                  <Box
                    component="span"
                    sx={{
                      fontSize: F.qty,
                      fontWeight: 800,
                      lineHeight: 1,
                      color: it.short ? C.late : C.text,
                    }}
                  >
                    {amount(it.total)}
                  </Box>
                  <Box component="span" sx={{ ml: '0.3vw', fontSize: F.small, color: C.muted }}>
                    {it.unit}
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  px: '0.9vw',
                  py: '0.8vh',
                  display: 'flex',
                  gap: '0.8vw',
                  fontSize: F.tiny,
                  color: C.muted,
                  bgcolor: it.short ? C.lateSoft : C.head,
                  borderTop: `1px solid ${it.short ? C.lateSoft : C.line}`,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {it.customers.length} 单 · {it.customers.join('、')}
                </Box>
                <Box
                  sx={{
                    flex: 'none',
                    fontWeight: 700,
                    color: it.short ? C.late : C.ok,
                  }}
                >
                  {it.short
                    ? `可用 ${amount(it.usable)}，缺 ${amount(it.short)}`
                    : `可用 ${amount(it.usable)}`}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </AutoScroll>
    </Panel>
  );
}

/** A newly confirmed order, announced across the top of the queue for a few seconds. */
function Arrival({ order }: { order: Ranked }) {
  return (
    <Box
      className="board-enter"
      sx={{
        mx: '0.8vw',
        mt: '0.8vw',
        px: '1vw',
        py: '1.2vh',
        borderRadius: 1,
        bgcolor: C.brand,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        gap: '0.8vw',
        flex: 'none',
      }}
    >
      <Box sx={{ fontSize: F.body, fontWeight: 800 }}>新订单</Box>
      <Box
        sx={{
          fontSize: F.title,
          fontWeight: 800,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
        }}
      >
        {order.customer}
      </Box>
      <Box sx={{ fontSize: F.body, opacity: 0.85, whiteSpace: 'nowrap' }}>
        {order.lines.length} 项 · {order.due.label}
      </Box>
      <Box sx={{ ml: 'auto', fontSize: F.small, opacity: 0.85, whiteSpace: 'nowrap' }}>
        {shortNumber(order)}
      </Box>
    </Box>
  );
}

export function PickingBoard({ workspaceName }: { workspaceName: string }) {
  const [data, setData] = useState<{ orders: Order[]; stock: Record<string, Stock> } | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [problem, setProblem] = useState<'' | 'offline' | 'expired'>('');
  const [now, setNow] = useState(() => new Date());
  const [fullscreen, setFullscreen] = useState(false);
  const [idle, setIdle] = useState(false);
  // Orders that arrived while the board was open: announced one at a time, then highlighted.
  const [arrived, setArrived] = useState<Record<string, number>>({});
  const [callingId, setCallingId] = useState<string | null>(null);
  const seen = useRef<Set<string> | null>(null);
  const queue = useRef<string[]>([]);
  const callStarted = useRef(0);
  const calling = useRef<string | null>(null);
  const audio = useRef<AudioContext | null>(null);

  // Data: poll, and diff against what was on screen to find new orders.
  useEffect(() => {
    let live = true;
    const load = async () => {
      try {
        const next = await api<{ orders: Order[]; stock?: Record<string, Stock> }>(
          '/board/picking',
        );
        if (!live) return;
        if (seen.current) {
          const fresh = next.orders.filter((o) => !seen.current!.has(o.id));
          if (fresh.length) {
            const at = Date.now();
            setArrived((prev) => ({
              ...prev,
              ...Object.fromEntries(fresh.map((o) => [o.id, at])),
            }));
            queue.current.push(...fresh.map((o) => o.id));
          }
        }
        seen.current = new Set(next.orders.map((o) => o.id));
        setData({ orders: next.orders, stock: next.stock ?? {} });
        setUpdatedAt(new Date());
        setProblem('');
      } catch (e) {
        if (live) setProblem((e as ApiError).status === 401 ? 'expired' : 'offline');
      }
    };
    void load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, []);

  // Clock, plus announcing queued new orders one by one.
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
      // Side effects (taking from the queue, the chime) stay out of state updaters, which
      // React may run twice.
      const showing = calling.current;
      if (showing && Date.now() - callStarted.current < CALL_MS) return;
      const next = queue.current.shift() ?? null;
      if (next === showing) return;
      calling.current = next;
      setCallingId(next);
      if (next) {
        callStarted.current = Date.now();
        chime(audio.current);
      }
    }, 1_000);
    return () => clearInterval(timer);
  }, []);

  // Keep the screen awake while visible; track fullscreen; hide the pointer when idle.
  useEffect(() => {
    let lock: { release: () => Promise<void> } | null = null;
    const wake = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        lock = await (navigator as any).wakeLock?.request('screen');
      } catch {
        // Not supported or not allowed; the display's own settings then apply.
      }
    };
    void wake();
    document.addEventListener('visibilitychange', wake);
    const onFull = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFull);
    let idleTimer = 0;
    const onMove = () => {
      setIdle(false);
      clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => setIdle(true), 3_000);
    };
    window.addEventListener('mousemove', onMove);
    onMove();
    return () => {
      void lock?.release();
      document.removeEventListener('visibilitychange', wake);
      document.removeEventListener('fullscreenchange', onFull);
      window.removeEventListener('mousemove', onMove);
      clearTimeout(idleTimer);
    };
  }, []);

  const goFullscreen = () => {
    // The click also unlocks sound for the new-order chime.
    audio.current ??= new AudioContext();
    void document.documentElement.requestFullscreen?.();
  };

  const today = day(now);
  const stock = data?.stock ?? {};
  const list = plan(
    (data?.orders ?? [])
      .map((o) => ({ ...o, due: urgency(o.deliveryDate, today) }))
      .sort((x, y) => x.due.rank - y.due.rank),
    stock,
  );
  const callingOrder = list.find((o) => o.id === callingId) ?? null;
  const shortSkus = new Set(
    list.flatMap((o) => o.lines.filter((l) => l.shortN > 0).map((l) => l.skuId)),
  );
  const stats = {
    total: list.length,
    items: list.reduce((s, o) => s + o.lines.length, 0),
    late: list.filter((o) => o.due.level === 'late').length,
    today: list.filter((o) => o.due.level === 'today').length,
    short: shortSkus.size,
  };
  const stale = updatedAt && now.getTime() - updatedAt.getTime() > 2 * REFRESH_MS;
  const kpis: [string, number, string, string][] = [
    ['待备货', stats.total, `单 · ${stats.items} 项`, C.text],
    ['已逾期', stats.late, '单', stats.late ? C.late : C.muted],
    ['今日交货', stats.today, '单', stats.today ? C.today : C.muted],
    ['缺货商品', stats.short, '种', stats.short ? C.late : C.muted],
  ];

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: C.page,
        color: C.text,
        display: 'flex',
        flexDirection: 'column',
        cursor: fullscreen && idle ? 'none' : 'default',
        fontVariantNumeric: 'tabular-nums',
        overflow: 'hidden',
      }}
    >
      {/* Header: who, what is waiting, and the time. */}
      <Box
        sx={{
          height: '9vh',
          minHeight: 64,
          px: '1.4vw',
          display: 'flex',
          alignItems: 'center',
          gap: '2vw',
          bgcolor: C.panel,
          borderBottom: `1px solid ${C.line}`,
          flex: 'none',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.8vw', minWidth: 0 }}>
          <BrandLogo size={Math.max(32, Math.round(window.innerWidth * 0.021))} />
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: F.title, fontWeight: 800, lineHeight: 1.2 }}>备货看板</Box>
            <Box
              sx={{
                fontSize: F.small,
                color: C.muted,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {workspaceName}
            </Box>
          </Box>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          {kpis.map(([label, value, unit, color], i) => (
            <Box
              key={label}
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '0.45vw',
                px: '1.6vw',
                borderLeft: i ? `1px solid ${C.line}` : 'none',
              }}
            >
              <Box sx={{ fontSize: F.body, color: C.muted }}>{label}</Box>
              <Box sx={{ fontSize: F.kpi, fontWeight: 800, color, lineHeight: 1 }}>{value}</Box>
              <Box sx={{ fontSize: F.small, color: C.muted, whiteSpace: 'nowrap' }}>{unit}</Box>
            </Box>
          ))}
        </Box>
        {/* Fullscreen switch, in the header so it never covers content; gone once fullscreen. */}
        {!fullscreen && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<FullscreenOutlined />}
            onClick={goFullscreen}
            sx={{ flex: 'none' }}
          >
            全屏
          </Button>
        )}
        <Box sx={{ textAlign: 'right', flex: 'none' }}>
          <Box sx={{ fontSize: F.kpi, fontWeight: 800, lineHeight: 1.05 }}>{clock(now)}</Box>
          <Box
            sx={{
              mt: '0.3vh',
              fontSize: F.tiny,
              fontWeight: stale || problem ? 700 : 400,
              color: stale || problem ? C.late : C.muted,
            }}
          >
            {problem === 'offline' || stale
              ? `网络中断，显示 ${updatedAt ? clock(updatedAt) : '—'} 的数据`
              : `${now.toLocaleDateString('zh-CN', { month: 'long', day: 'numeric', weekday: 'short' })} · 更新于 ${updatedAt ? clock(updatedAt) : '—'}`}
          </Box>
        </Box>
      </Box>

      {/* Body: the queue of orders, and per-item totals. */}
      <Box sx={{ flex: 1, minHeight: 0, p: '1vw' }}>
        {problem === 'expired' ? (
          <Notice
            title="登录已过期"
            detail="请用只读账号重新登录后再全屏。"
            action={
              <Button variant="contained" size="large" onClick={() => window.location.reload()}>
                重新登录
              </Button>
            }
          />
        ) : data === null ? (
          <Notice title="正在加载…" />
        ) : !list.length ? (
          <Notice title="暂无待备货订单" detail="双方确认的销售订单会自动出现在这里。" />
        ) : (
          <Box
            sx={{
              height: '100%',
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 2.75fr) minmax(0, 1fr)',
              gap: '1vw',
            }}
          >
            <Panel title="备货队列" count={`${list.length} 单`} extra="按交货日期排序">
              {callingOrder && <Arrival order={callingOrder} />}
              <AutoScroll
                watch={list
                  .map((o) => `${o.id}:${o.lines.map((l) => `${l.remaining}/${l.shortN}`).join()}`)
                  .join('|')}
              >
                <Box sx={{ p: '0.8vw', display: 'grid', gap: '0.8vw' }}>
                  {list.map((o) => (
                    <OrderCard key={o.id} order={o} now={now} arrived={arrived[o.id]} />
                  ))}
                </Box>
              </AutoScroll>
            </Panel>
            <Totals orders={list} stock={stock} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

function Notice(p: { title: string; detail?: string; action?: ReactNode }) {
  return (
    <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', textAlign: 'center' }}>
      <Box>
        <Box sx={{ fontSize: F.kpi, fontWeight: 800 }}>{p.title}</Box>
        {p.detail && <Box sx={{ mt: '1.5vh', fontSize: F.body, color: C.muted }}>{p.detail}</Box>}
        {p.action && <Box sx={{ mt: '3vh' }}>{p.action}</Box>}
      </Box>
    </Box>
  );
}
